/**
 * The shape of the realm — the geometry the world map is drawn from.
 *
 * The province table carries a position, a terrain and a list of neighbours.
 * That is a graph, and a graph drawn honestly is circles joined by lines. This
 * module turns the same table into a *chart*: a landmass with a coastline, a
 * partition of that landmass into territories that share their borders, roads
 * that bend, and a scatter of terrain marks to hang hatching on.
 *
 * Three rules govern everything here.
 *
 * 1. **It is presentation, so it lives in `src/data`, not `src/sim`.** Nothing
 *    in here has simulation meaning. The campaign walks `links`; it has never
 *    heard of a coastline.
 * 2. **It is deterministic.** There is no `Math.random` and there never may be.
 *    Every irregularity comes from a hashed value-noise field or from the fixed
 *    bay table below, so the realm looks the same every time it is opened, in
 *    every build, on every machine.
 * 3. **It is computed once.** Everything is memoised behind a lazy `geometry()`
 *    call, because none of it depends on the state of the campaign — only on
 *    the province table, which is content.
 *
 * The pipeline, in order:
 *
 *   sites → support-function coast → Voronoi cells clipped to the coast
 *         → densify every edge → warp the whole plane → draw
 *
 * The warp is the trick that makes it look drawn rather than generated. It is a
 * smooth 2-D displacement field applied to *every* point at the very end, so
 * straight Voronoi bisectors become bent borders, the coast gains headlands,
 * and — the part that matters — two cells that shared an edge before the warp
 * still share it after, because the same field moved both copies to the same
 * place. Borders stay welded. Nothing gapes.
 */

import { PROVINCES } from './provinces.ts';
import { NATIONS } from './nations.ts';
import { hash2, vnoise, RASTER, SEA_LANES, WORLD } from './realm.ts';

export interface Pt {
  x: number;
  y: number;
}

/** One terrain mark: where to draw it, how big, and which variant. */
export interface TerrainMark {
  x: number;
  y: number;
  /** Roughly the half-width of the glyph, in map units. */
  r: number;
  /** 0..3 — lets the drawing code vary the shape without needing its own dice. */
  v: number;
}

/** A stretch of border shared by two provinces, or by a province and the sea. */
export interface BorderSeg {
  a: number;
  /** The province on the other side, or -1 for coastline. */
  b: number;
  points: Pt[];
}

// ── The frame ──────────────────────────────────────────────────────────────
//
// The world is a fixed sheet of paper now — `WORLD` in `realm.ts` — rather than
// a bounding box measured off wherever the provinces happened to fall, because
// the ocean is part of the map and the ocean has no provinces in it. Everything
// in this file is still in map units and every constant tuned when there were
// twenty-four holdings still means what it meant: a province is about eighteen
// units across on this board too.

/** A little air round the sheet, for the warp and for the frame line. */
const SHEET_MARGIN = 9;

export const VIEW = {
  x: WORLD.originX - SHEET_MARGIN,
  y: WORLD.originY - SHEET_MARGIN,
  w: WORLD.w + SHEET_MARGIN * 2,
  h: WORLD.h + SHEET_MARGIN * 2,
};
export const MAP_VIEWBOX = `${VIEW.x.toFixed(1)} ${VIEW.y.toFixed(1)} ${VIEW.w.toFixed(1)} ${VIEW.h.toFixed(1)}`;

/** For the scale bar. Invented, consistent, and nobody can prove otherwise. */
export const LEAGUES_PER_UNIT = 2.5;

/** Marks are kept this far clear of a border, so hatching never crosses one. */
const MARK_INSET = 1.9;
/** …and this far clear of the town, which needs room for its own furniture. */
const TOWN_CLEARING = 6.2;

/**
 * The plane warp. Two octaves, and their amplitudes are deliberately small
 * relative to their wavelengths: the field has to stay injective or a cell can
 * fold through its own border. Worst-case gradient here is about 0.55, which
 * leaves a comfortable margin under 1.
 *
 * It matters less than it did — the coastline is no longer a smooth analytic
 * ring that needed roughening, it is a traced contour with detail already in it
 * — but it is what stops the *borders* between two provinces reading as the
 * smoothed staircases they are underneath, and it is what keeps a road from
 * looking like a ruled line.
 */
const WARP = [
  { f: 0.036, a: 3.3, s: 11 },
  { f: 0.13, a: 0.9, s: 41 },
  /**
   * A third octave, added by the second chart pass, and it is the half of the
   * coastline fix that `densify` does not do on its own.
   *
   * Densifying before the warp gives the field points to act on along a lattice
   * straight; this is what it acts *with*. Measured on the pass that added
   * `densify` alone, the world's shoreline still ran 35.9% within six degrees of
   * an axis with 22-unit stretches that never turned three, because the two
   * octaves above bend a straight line by a sagitta of about 0.002·L² — under
   * the drawing tolerance for anything shorter than twelve units. A wave five
   * units long and two thirds of a unit tall is over that tolerance everywhere.
   *
   * It costs injectivity, which is the only thing that matters here, and the
   * cost is printed: `npm run coastline` sweeps the sheet and reports the
   * smallest Jacobian determinant of this field. It was 0.385 with two octaves
   * and must stay well clear of zero, because zero is a border folded through
   * itself.
   */
  { f: 0.21, a: 0.62, s: 73 },
];

export function warp(p: Pt): Pt {
  let dx = 0;
  let dy = 0;
  for (const o of WARP) {
    dx += (vnoise(p.x * o.f, p.y * o.f, o.s) - 0.5) * 2 * o.a;
    dy += (vnoise(p.x * o.f, p.y * o.f, o.s + 7) - 0.5) * 2 * o.a;
  }
  return { x: p.x + dx, y: p.y + dy };
}

/**
 * …and the same field run backwards: given a point on the drawn sheet, where on
 * the raster did it come from?
 *
 * **This is what stops the chart and the paint disagreeing.** Every line on this
 * map is warped and every *raster* laid under it — the hillshade, the fabric,
 * the strand, the sea floor — is not, so the drawn coastline has always sat some
 * way off the land/water boundary the strand was painted from. `npm run
 * coastline` measures it: median 2.0 map units, up to 6. Survivable while the
 * only thing following the raster was a shading; not survivable once a beach, a
 * snowline and a per-cell biome wash are being painted at a hard edge, and not
 * survivable at all once the coastline is densified before the warp, which is
 * what fixes its shape and which moves the *middle* of every straight stretch
 * rather than only its ends.
 *
 * So the bakes sample through this instead: pixel `q` on the sheet reads the
 * field at `unwarp(q)`, and the painted shore lands exactly under the drawn one.
 *
 * The method is the plain fixed point of `p ← q − W(p)`, which converges because
 * `W` is a contraction — the warp's amplitudes were chosen small relative to
 * their wavelengths precisely so the field stays injective, and |∇W| ≈ 0.55
 * leaves a comfortable margin under 1. Twelve iterations put 0.55¹² ≈ 0.001 of
 * the initial four units of error on the table, which is a thousandth of a map
 * unit; the loop stops early when it gets there, so most points cost six.
 */
export function unwarp(q: Pt): Pt {
  let x = q.x;
  let y = q.y;
  for (let i = 0; i < 12; i++) {
    let dx = 0;
    let dy = 0;
    for (const o of WARP) {
      dx += (vnoise(x * o.f, y * o.f, o.s) - 0.5) * 2 * o.a;
      dy += (vnoise(x * o.f, y * o.f, o.s + 7) - 0.5) * 2 * o.a;
    }
    const nx = q.x - dx;
    const ny = q.y - dy;
    const moved = Math.abs(nx - x) + Math.abs(ny - y);
    x = nx;
    y = ny;
    if (moved < 1e-3) break;
  }
  return { x, y };
}

// ── Small geometry ─────────────────────────────────────────────────────────

function dist(a: Pt, b: Pt): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function inside(poly: Pt[], p: Pt): boolean {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i];
    const b = poly[j];
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) hit = !hit;
  }
  return hit;
}

/** Distance from a point to the nearest edge of a polygon. */
function edgeDistance(poly: Pt[], p: Pt): number {
  let best = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[j];
    const b = poly[i];
    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const len2 = vx * vx + vy * vy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2)) : 0;
    best = Math.min(best, Math.hypot(p.x - (a.x + vx * t), p.y - (a.y + vy * t)));
  }
  return best;
}

function centroid(poly: Pt[]): Pt {
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[j];
    const b = poly[i];
    const f = a.x * b.y - b.x * a.y;
    area += f;
    cx += (a.x + b.x) * f;
    cy += (a.y + b.y) * f;
  }
  if (Math.abs(area) < 1e-9) return poly[0] ?? { x: 0, y: 0 };
  return { x: cx / (3 * area), y: cy / (3 * area) };
}

function ringArea(poly: Pt[]): number {
  let area = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    area += poly[j].x * poly[i].y - poly[i].x * poly[j].y;
  }
  return Math.abs(area) / 2;
}

// ── Every line on the chart, traced off the ground ─────────────────────────
//
// This is the part of the file that was thrown away and rebuilt, and the reason
// is worth stating once because everything below follows from it.
//
// It used to take a Voronoi of the province sites, clip every cell against a
// smooth analytic coastline ring, and give the inland seas sites of their own so
// they would not be painted as farmland. That produced three faults that no
// amount of tuning could reach. Every cell was a convex polygon of five or six
// straight sides, so a *province* was a shape a machine makes. The coastline was
// the support function of the site scatter plus some noise, so it was a slightly
// dented circle around everything — the outline of a blob. And the sites came
// off a lattice, so the whole partition read as rows and columns.
//
// `realm.ts` now decides the world on a raster two units to a cell — a ninth of
// a province — and every line here is traced off it:
//
//   raster → boundary edges → arcs between junctions → smooth, simplify, warp
//          → rings assembled from arcs
//
// The arcs are the trick. A stretch of boundary between two regions is smoothed
// *once* and handed to both of them, so two territories cannot gape however hard
// the smoothing bends the line, and the coastline in the picture is the same
// polyline as the edge of the province standing on it. There is no separate
// coast function any more, and there is nothing left in the pipeline with a row
// or a column in it.

/**
 * The pitch a line is resampled at before the plane warp is applied to it, in
 * map units, and how many binomial passes smooth it first.
 *
 * `DRAW_STEP` has to sit well inside the warp's shortest wavelength — 1/0.13,
 * about 7.7 units — or the warp is being sampled below Nyquist and a stretch of
 * coast comes out as a chord across a bend that is really there. One unit is
 * seven or eight samples to a wave, and the Douglas–Peucker pass afterwards
 * gives most of them straight back on the stretches that stayed straight.
 *
 * `WIDE_PASSES` at a post-chaikin spacing of about half a unit is σ ≈ 1 map
 * unit: half a raster cell, enough to stop the two-unit staircase reading as
 * steps, small enough to leave a headland where the ground put it.
 */
const DRAW_STEP = 1;
const WIDE_PASSES = 8;

const RCOLS = RASTER.cols;
const RROWS = RASTER.rows;
const RCELL = RASTER.cell;
/** Corner lattice is one wider than the cell lattice in each axis. */
const CW = RCOLS + 1;

function ownerAt(col: number, row: number): number {
  if (col < 0 || row < 0 || col >= RCOLS || row >= RROWS) return -1;
  return RASTER.owner[row * RCOLS + col];
}

function cornerPt(id: number): Pt {
  const c = id % CW;
  const r = (id - c) / CW;
  return { x: RASTER.originX + c * RCELL, y: RASTER.originY + r * RCELL };
}

/**
 * A stretch of boundary shared by exactly two regions, from one junction to the
 * next. `lo` is the lower region index and is -1 when the far side is water;
 * `hi` is always a province.
 */
interface Arc {
  lo: number;
  hi: number;
  /** Corner ids, in order. */
  ends: number[];
  /** The drawn polyline, smoothed and warped. */
  pts: Pt[];
  /** …and the same line before the warp, for the inside-and-clearance tests. */
  flat: Pt[];
  closed: boolean;
}

/** Chaikin corner-cutting: the staircase of a raster becomes a drawn line. */
function chaikin(pts: Pt[], closed: boolean, passes: number): Pt[] {
  let out = pts;
  for (let p = 0; p < passes && out.length > 2; p++) {
    const next: Pt[] = [];
    const n = out.length;
    if (!closed) next.push(out[0]);
    const last = closed ? n : n - 1;
    for (let i = 0; i < last; i++) {
      const a = out[i];
      const b = out[(i + 1) % n];
      next.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
      next.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
    }
    if (!closed) next.push(out[n - 1]);
    out = next;
  }
  return out;
}

/**
 * Resample a polyline so no segment is longer than `step`.
 *
 * **This is the fix for the coastline's shape, and it is worth being precise
 * about why, because the obvious diagnosis was wrong.**
 *
 * The first chart pass left the coast running "in straight lattice stretches
 * with rounded corners" and guessed the cure was smoothing at a wider scale than
 * `chaikin(…, 3)`. Measured, the fault was somewhere else entirely: 41.3% of the
 * world's shoreline ran within six degrees of due north or due east — where a
 * direction picked at random gives 13% — and the longest run that never turned
 * three degrees was **forty map units**, two provinces wide, with single
 * segments up to 33.7 units long.
 *
 * A thirty-three-unit segment is the tell. The pipeline used to run
 * `simplify(chaikin(raw), 0.3)` and *then* warp, and Douglas–Peucker's whole job
 * is to collapse a straight run to its two endpoints — so by the time the warp
 * got there, a seven-cell lattice straight had no interior points left to bend.
 * The warp moved the two ends and the ruled line between them stayed ruled. The
 * coast was not under-smoothed; it was under-*sampled*, and smoothing it wider
 * would have made a smoother ruler.
 *
 * So the order is now densify → warp → simplify. The line is resampled at a
 * pitch well inside the warp's shortest wavelength (7.7 units), every one of
 * those points is displaced, and only then is the result thinned — against the
 * *warped* geometry, so the thinning keeps the bends instead of erasing them.
 * The smoothing passes and the tolerance are untouched, and so are the warp's
 * amplitudes: nothing here makes the field any less injective than it was.
 */
function densify(pts: Pt[], closed: boolean, step: number): Pt[] {
  if (pts.length < 2) return pts;
  const out: Pt[] = [];
  const last = closed ? pts.length : pts.length - 1;
  for (let i = 0; i < last; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    out.push(a);
    const l = Math.hypot(b.x - a.x, b.y - a.y);
    const cuts = Math.floor(l / step);
    for (let k = 1; k <= cuts; k++) {
      const t = k / (cuts + 1);
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
  }
  if (!closed) out.push(pts[pts.length - 1]);
  return out;
}

/**
 * A wider stencil than corner-cutting: a binomial blur along the line, with the
 * ends pinned.
 *
 * Chaikin converges on a quadratic B-spline of its control polygon, so running
 * it more times refines the curve without smoothing it any further — three
 * passes and thirty passes round a cell corner by the same amount. Taking the
 * cell-scale staircase out from under the drawn line needs an actual filter,
 * and this is the cheapest one: `¼ ½ ¼` repeated, which after `n` passes is a
 * Gaussian of σ ≈ spacing·√(n/2).
 *
 * **The ends are pinned and that is not a detail.** Two arcs meeting at a
 * junction are two different lines whose shared corner must land on the same
 * point to the last bit, or the province rings chained out of them gape. Every
 * operation in this pipeline preserves endpoints exactly for that reason —
 * chaikin on an open line, Douglas–Peucker, `densify`, and this.
 *
 * Kept modest on purpose: σ is about a map unit, which is half a raster cell.
 * It is enough to stop the staircase reading as steps and small enough that a
 * headland is still where the ground put it — the drawn line is measured
 * against the mask it was traced from by `npm run coastline`, and the whole
 * point of this pass is that that number gets *smaller*, not larger.
 */
function smoothWide(pts: Pt[], closed: boolean, passes: number): Pt[] {
  if (pts.length < 5) return pts;
  let cur = pts;
  const n = pts.length;
  for (let p = 0; p < passes; p++) {
    const next: Pt[] = new Array<Pt>(n);
    for (let i = 0; i < n; i++) {
      if (!closed && (i === 0 || i === n - 1)) {
        next[i] = cur[i];
        continue;
      }
      const a = cur[(i - 1 + n) % n];
      const b = cur[i];
      const c = cur[(i + 1) % n];
      next[i] = { x: a.x * 0.25 + b.x * 0.5 + c.x * 0.25, y: a.y * 0.25 + b.y * 0.5 + c.y * 0.25 };
    }
    cur = next;
  }
  return cur;
}

/**
 * Ramer–Douglas–Peucker, on the smoothed line.
 *
 * Corner-cutting multiplies the point count by eight and most of those points
 * are on a line the eye cannot tell from straight. Thinning them afterwards is
 * the difference between a map screen that parses two hundred kilobytes of path
 * data and one that parses eight hundred. It is done to the *arc*, so both sides
 * of a border are thinned identically and the weld survives.
 */
function simplify(pts: Pt[], tol: number): Pt[] {
  if (pts.length < 3) return pts;
  const keep = new Uint8Array(pts.length);
  keep[0] = 1;
  keep[pts.length - 1] = 1;
  const stack: Array<[number, number]> = [[0, pts.length - 1]];
  while (stack.length > 0) {
    const [a, b] = stack.pop() as [number, number];
    if (b - a < 2) continue;
    const ax = pts[a].x;
    const ay = pts[a].y;
    const bx = pts[b].x;
    const by = pts[b].y;
    const len = Math.hypot(bx - ax, by - ay);
    let worst = -1;
    let worstD = tol;
    for (let i = a + 1; i < b; i++) {
      const d =
        len < 1e-9
          ? Math.hypot(pts[i].x - ax, pts[i].y - ay)
          : Math.abs((bx - ax) * (ay - pts[i].y) - (ax - pts[i].x) * (by - ay)) / len;
      if (d > worstD) {
        worstD = d;
        worst = i;
      }
    }
    if (worst < 0) continue;
    keep[worst] = 1;
    stack.push([a, worst], [worst, b]);
  }
  return pts.filter((_, i) => keep[i] === 1);
}

interface Complex {
  arcs: Arc[];
  /** Arc indices touching each province. */
  byProvince: number[][];
  /** Arc indices with water on the far side. */
  shoreArcs: number[];
}

/** Every boundary in the world, traced once and shared by both sides of it. */
function traceComplex(): Complex {
  interface Edge {
    u: number;
    v: number;
    lo: number;
    hi: number;
  }
  const edges: Edge[] = [];
  const at = (col: number, row: number): number => row * CW + col;
  const push = (u: number, v: number, a: number, b: number): void => {
    edges.push({ u, v, lo: Math.min(a, b), hi: Math.max(a, b) });
  };
  for (let row = 0; row < RROWS; row++) {
    for (let col = 0; col <= RCOLS; col++) {
      const a = ownerAt(col, row);
      const b = ownerAt(col - 1, row);
      if (a !== b) push(at(col, row), at(col, row + 1), a, b);
    }
  }
  for (let row = 0; row <= RROWS; row++) {
    for (let col = 0; col < RCOLS; col++) {
      const a = ownerAt(col, row);
      const b = ownerAt(col, row - 1);
      if (a !== b) push(at(col, row), at(col + 1, row), a, b);
    }
  }

  const incident = new Map<number, number[]>();
  const add = (corner: number, e: number): void => {
    const list = incident.get(corner);
    if (list) list.push(e);
    else incident.set(corner, [e]);
  };
  edges.forEach((e, i) => {
    add(e.u, i);
    add(e.v, i);
  });

  const used = new Uint8Array(edges.length);
  const arcs: Arc[] = [];

  const other = (e: Edge, corner: number): number => (e.u === corner ? e.v : e.u);
  /** The one edge continuing the same pair through a degree-two corner. */
  const step = (corner: number, from: number): number => {
    const list = incident.get(corner) ?? [];
    if (list.length !== 2) return -1;
    for (const e of list) if (e !== from && !used[e]) return e;
    return -1;
  };

  /**
   * One traced boundary, turned into a drawn line.
   *
   * Corner-cut, then blurred at a wider stencil than corner-cutting can reach,
   * then **densified before the warp and thinned after it** — see `densify` for
   * the measurement that says why that order is the whole of the coastline's
   * shape. `flat` is the same line before the warp, thinned on its own, and it
   * is what every inside-and-clearance test in this file reads.
   */
  const finish = (ends: number[], lo: number, hi: number, closed: boolean): void => {
    const flatRaw = ends.map(cornerPt);
    const smooth = smoothWide(chaikin(flatRaw, closed, 3), closed, WIDE_PASSES);
    const flat = simplify(smooth, 0.3);
    const pts = simplify(densify(smooth, closed, DRAW_STEP).map(warp), 0.3);
    arcs.push({ lo, hi, ends, pts, flat, closed });
  };

  // Open arcs first, from every junction.
  for (const [corner, list] of [...incident.entries()].sort((a, b) => a[0] - b[0])) {
    if (list.length === 2) continue;
    for (const start of list) {
      if (used[start]) continue;
      used[start] = 1;
      const e0 = edges[start];
      const ends = [corner, other(e0, corner)];
      let cur = start;
      for (;;) {
        const tip = ends[ends.length - 1];
        const nxt = step(tip, cur);
        if (nxt < 0) break;
        used[nxt] = 1;
        ends.push(other(edges[nxt], tip));
        cur = nxt;
      }
      finish(ends, e0.lo, e0.hi, false);
    }
  }
  // …then whatever is left, which is a closed loop with no junction on it: the
  // shore of an island held by one province, or a border that rings another.
  for (let i = 0; i < edges.length; i++) {
    if (used[i]) continue;
    used[i] = 1;
    const e0 = edges[i];
    const ends = [e0.u, e0.v];
    let cur = i;
    for (;;) {
      const tip = ends[ends.length - 1];
      if (tip === ends[0]) break;
      const nxt = step(tip, cur);
      if (nxt < 0) break;
      used[nxt] = 1;
      ends.push(other(edges[nxt], tip));
      cur = nxt;
    }
    if (ends[ends.length - 1] === ends[0]) ends.pop();
    finish(ends, e0.lo, e0.hi, true);
  }

  const byProvince: number[][] = PROVINCES.map(() => []);
  const shoreArcs: number[] = [];
  arcs.forEach((a, i) => {
    if (a.hi >= 0 && a.hi < byProvince.length) byProvince[a.hi].push(i);
    if (a.lo >= 0 && a.lo < byProvince.length) byProvince[a.lo].push(i);
    if (a.lo < 0) shoreArcs.push(i);
  });
  return { arcs, byProvince, shoreArcs };
}

/**
 * Chain a set of arcs into closed rings by their endpoints.
 *
 * Every corner on a region's boundary has exactly two arcs of that boundary
 * running through it — `realm.ts` guarantees it by refusing to leave a corner
 * where four regions meet in a checkerboard — so the chaining is unambiguous and
 * needs no geometry, only bookkeeping.
 */
function chainRings(
  arcs: Arc[],
  ids: number[],
  pick: (a: Arc) => Pt[],
): Pt[][] {
  const rings: Pt[][] = [];
  const atCorner = new Map<number, number[]>();
  const open: number[] = [];
  for (const i of ids) {
    const a = arcs[i];
    if (a.closed) {
      rings.push(pick(a).slice());
      continue;
    }
    open.push(i);
    for (const end of [a.ends[0], a.ends[a.ends.length - 1]]) {
      const list = atCorner.get(end);
      if (list) list.push(i);
      else atCorner.set(end, [i]);
    }
  }
  const used = new Set<number>();
  for (const start of open) {
    if (used.has(start)) continue;
    const ring: Pt[] = [];
    let cur = start;
    let head = arcs[cur].ends[0];
    for (;;) {
      used.add(cur);
      const a = arcs[cur];
      const forward = a.ends[0] === head;
      const pts = pick(a);
      const run = forward ? pts : pts.slice().reverse();
      for (let i = 0; i < run.length - 1; i++) ring.push(run[i]);
      head = forward ? a.ends[a.ends.length - 1] : a.ends[0];
      const list = atCorner.get(head) ?? [];
      let nxt = -1;
      for (const cand of list) if (cand !== cur && !used.has(cand)) nxt = cand;
      if (nxt < 0) break;
      cur = nxt;
    }
    if (ring.length >= 3) rings.push(ring);
  }
  return rings;
}

/**
 * The towns, at the seats `realm.ts` sampled.
 *
 * No jitter is added here any more. There used to be some, because the sites
 * came off a lattice and a Voronoi of a lattice is a lattice; they now come off
 * a Poisson-disc sample of the land, which is already irregular, and a seat that
 * wandered would only risk leaving the territory it names.
 */
const SITES: Pt[] = PROVINCES.map((p) => ({ x: p.x, y: p.y }));

/** How far out to sea a point is, in map units, off the realm's own raster. */
function offshoreAt(p: Pt): number {
  const col = Math.floor((p.x - RASTER.originX) / RCELL);
  const row = Math.floor((p.y - RASTER.originY) / RCELL);
  if (col < 0 || row < 0 || col >= RCOLS || row >= RROWS) return 999;
  return RASTER.offshore[row * RCOLS + col];
}

/**
 * The engraved sea lines, as real contours of the water rather than as echoes of
 * one ring.
 *
 * A chart draws two or three lines parallel to the shore because that is what an
 * engraver could do with a burin, and it is the single cheapest thing that makes
 * water read as water. The old version offset one analytic coastline outward,
 * which works when the land is one blob and produces nonsense when it is eight —
 * the offsets of two neighbouring islands cross each other. These are marching-
 * squares contours of the distance-to-land field, so every landmass gets its own
 * set, they never cross, and a strait narrower than the band simply does not get
 * one, which is exactly right.
 */
function contour(level: number): Pt[][] {
  const seg: Array<[Pt, Pt]> = [];
  const val = (col: number, row: number): number =>
    col < 0 || row < 0 || col >= RCOLS || row >= RROWS ? 999 : RASTER.offshore[row * RCOLS + col];
  const px = (col: number): number => RASTER.originX + (col + 0.5) * RCELL;
  const py = (row: number): number => RASTER.originY + (row + 0.5) * RCELL;
  const lerp = (a: number, b: number, va: number, vb: number): number =>
    Math.abs(vb - va) < 1e-9 ? a : a + ((level - va) / (vb - va)) * (b - a);

  for (let row = -1; row < RROWS; row++) {
    for (let col = -1; col < RCOLS; col++) {
      const v00 = val(col, row);
      const v10 = val(col + 1, row);
      const v01 = val(col, row + 1);
      const v11 = val(col + 1, row + 1);
      const code =
        (v00 > level ? 1 : 0) | (v10 > level ? 2 : 0) | (v11 > level ? 4 : 0) | (v01 > level ? 8 : 0);
      if (code === 0 || code === 15) continue;
      const top = { x: lerp(px(col), px(col + 1), v00, v10), y: py(row) };
      const right = { x: px(col + 1), y: lerp(py(row), py(row + 1), v10, v11) };
      const bottom = { x: lerp(px(col), px(col + 1), v01, v11), y: py(row + 1) };
      const left = { x: px(col), y: lerp(py(row), py(row + 1), v00, v01) };
      switch (code) {
        case 1:
        case 14:
          seg.push([left, top]);
          break;
        case 2:
        case 13:
          seg.push([top, right]);
          break;
        case 3:
        case 12:
          seg.push([left, right]);
          break;
        case 4:
        case 11:
          seg.push([right, bottom]);
          break;
        case 6:
        case 9:
          seg.push([top, bottom]);
          break;
        case 7:
        case 8:
          seg.push([left, bottom]);
          break;
        // The saddles. Resolved the same way every time, which is all that is
        // asked of them: a wrong guess joins two lobes of one contour and reads
        // as a line an engraver drew slightly differently.
        case 5:
          seg.push([left, top], [right, bottom]);
          break;
        case 10:
          seg.push([top, right], [left, bottom]);
          break;
        default:
          break;
      }
    }
  }

  // Chain the segments into polylines by their shared ends.
  const key = (p: Pt): string => `${p.x.toFixed(3)},${p.y.toFixed(3)}`;
  const ends = new Map<string, number[]>();
  seg.forEach(([a, b], i) => {
    for (const p of [a, b]) {
      const k = key(p);
      const list = ends.get(k);
      if (list) list.push(i);
      else ends.set(k, [i]);
    }
  });
  const used = new Uint8Array(seg.length);
  const out: Pt[][] = [];
  for (let i = 0; i < seg.length; i++) {
    if (used[i]) continue;
    used[i] = 1;
    const line: Pt[] = [seg[i][0], seg[i][1]];
    for (let end = 0; end < 2; end++) {
      for (;;) {
        const tip = end === 0 ? line[line.length - 1] : line[0];
        const list = ends.get(key(tip)) ?? [];
        let nxt = -1;
        for (const c of list) if (!used[c]) nxt = c;
        if (nxt < 0) break;
        used[nxt] = 1;
        const [a, b] = seg[nxt];
        const far = key(a) === key(tip) ? b : a;
        if (end === 0) line.push(far);
        else line.unshift(far);
      }
      if (line.length > 2 && key(line[0]) === key(line[line.length - 1])) break;
    }
    if (line.length >= 3) out.push(chaikin(line, false, 1).map(warp));
  }
  return out;
}

// ── The whole thing, computed once ─────────────────────────────────────────

interface Geometry {
  /** The land, as closed rings: one per landmass, plus reversed rings for any
   *  water it encloses, so a non-zero fill paints exactly the dry ground. */
  coast: Pt[][];
  /** Contours of the water at four depths, for the engraved sea lines. */
  echoes: Pt[][][];
  /** One warped ring per province. */
  cells: Pt[][];
  /** Where the town is drawn — the warped site. */
  sites: Pt[];
  /** Warped centroid of the territory, for nation lettering. */
  centres: Pt[];
  /** Where the name sits, and whether it went above the town instead of below. */
  labels: Array<Pt & { above: boolean }>;
  /** Shared borders, each listed once, lower province index first. */
  borders: BorderSeg[];
  /** Terrain hatching positions, per province. */
  marks: TerrainMark[][];
  /** True if the province touches the sea. `realm.ts` is the authority. */
  coastal: boolean[];
  /** Wave ticks, scattered over open water only. */
  waves: Pt[];
  /** The stretch of shore each province shares with the water. */
  shores: BorderSeg[];
}

let cached: Geometry | null = null;

function build(): Geometry {
  const complex = traceComplex();
  const { arcs } = complex;

  // ── Territory ────────────────────────────────────────────────────────────
  // A province's ring is its own arcs chained end to end. Two provinces that
  // share a border share the *same arc object*, so the line between them is one
  // polyline drawn once and there is nothing for the two sides to disagree
  // about — which is the property the old Voronoi clipper had to be talked into
  // with a re-tagging pass and a verifier, and gets here for free.
  const flat: Pt[][] = [];
  const cells: Pt[][] = [];
  for (let i = 0; i < PROVINCES.length; i++) {
    const warped = chainRings(arcs, complex.byProvince[i], (a) => a.pts);
    const plain = chainRings(arcs, complex.byProvince[i], (a) => a.flat);
    // A province with more than one ring has ground round a hole in it, which
    // the growth in `realm.ts` tries hard not to leave. Draw the largest.
    let best = 0;
    for (let k = 1; k < warped.length; k++) {
      if (ringArea(warped[k]) > ringArea(warped[best])) best = k;
    }
    cells.push(warped[best] ?? []);
    flat.push(plain[best] ?? []);
  }

  // ── Borders and shores ───────────────────────────────────────────────────
  const borders: BorderSeg[] = [];
  const shores: BorderSeg[] = [];
  for (const a of arcs) {
    if (a.pts.length < 2) continue;
    if (a.lo < 0) shores.push({ a: a.hi, b: -1, points: a.pts });
    else borders.push({ a: a.lo, b: a.hi, points: a.pts });
  }

  // ── The coast, and the water beyond it ───────────────────────────────────
  const coast = chainRings(arcs, complex.shoreArcs, (a) => a.pts);
  const echoes = [2.6, 5.4, 9, 14].map(contour);

  // ── Sites, labels, centroids ─────────────────────────────────────────────
  const sites = SITES.map(warp);
  const centres = flat.map((poly, i) => (poly.length >= 3 ? warp(centroid(poly)) : sites[i]));
  const labels = placeLabels(flat);

  // ── Terrain marks ────────────────────────────────────────────────────────
  const marks = flat.map((poly, i) => scatter(poly, SITES[i], i));

  // ── Open water ───────────────────────────────────────────────────────────
  // Wave ticks on a jittered grid, everywhere far enough out that a tick cannot
  // be read as a rock. "Far enough out" is a fact about the water now, so a
  // narrow strait gets none and the open ocean gets all of them.
  const waves: Pt[] = [];
  const gap = 7.6;
  for (let gy = VIEW.y + 4; gy < VIEW.y + VIEW.h - 4; gy += gap) {
    for (let gx = VIEW.x + 4; gx < VIEW.x + VIEW.w - 4; gx += gap) {
      const jx = hash2(Math.round(gx), Math.round(gy), 601);
      const jy = hash2(Math.round(gx) + 13, Math.round(gy) + 7, 601);
      const p = { x: gx + (jx - 0.5) * gap * 0.7, y: gy + (jy - 0.5) * gap * 0.7 };
      if (offshoreAt(p) < 13) continue;
      waves.push(p);
    }
  }

  return {
    coast,
    echoes,
    cells,
    sites,
    centres,
    labels,
    borders,
    marks,
    coastal: PROVINCES.map((p) => p.coastal),
    waves,
    shores,
  };
}


/**
 * Where each name goes.
 *
 * Below the town by default, above it where the cell is short of room down
 * there, and shoved a little further out where the obvious spot is already
 * taken by another name or by a neighbouring town's mark. Longest names are
 * placed first: a long name has the fewest places it can go, and letting
 * "Ashfall" claim ground that "The Cold Anvil" needed is how you end up with
 * two names lying on top of each other.
 */
function placeLabels(raw: Pt[][]): Array<Pt & { above: boolean }> {
  const out: Array<Pt & { above: boolean }> = new Array(raw.length);
  // Every *other* town's mark is an obstacle from the start — a name over
  // somebody else's keep is worse than a name a little off-centre. A name over
  // its own keep is not possible: it is placed clear of it by construction, and
  // treating its own mark as an obstacle only ever shoved it further away.
  const marks: Array<{ x: number; y: number; hw: number; hh: number }> = SITES.map((s) => ({
    x: s.x,
    y: s.y,
    hw: 3.9,
    hh: 2.9,
  }));
  const taken: Array<{ x: number; y: number; hw: number; hh: number }> = [];

  // Seats first, then the longest names. A long name has the fewest places it
  // can go, and letting "Ashfall" claim ground that "The Cold Anvil" needed is
  // how you end up with two names lying on top of each other — but a seat's name
  // is the one that has to be legible at every zoom, so it chooses before
  // anybody.
  const order = raw
    .map((_, i) => i)
    .sort(
      (a, b) =>
        labelRank(a) - labelRank(b) ||
        PROVINCES[b].name.length - PROVINCES[a].name.length ||
        a - b,
    );

  for (const i of order) {
    const poly = raw[i];
    const s = SITES[i];
    // 1.05 units of box per character, tracking the map screen's type at its
    // half-again-bigger sizes — a placement box narrower than the name it
    // places is how two readable names end up printed through each other.
    const hw = PROVINCES[i].name.length * 1.05 + 0.8;
    const hh = 2.1;
    const tries: Array<Pt & { above: boolean }> = [
      { x: s.x, y: s.y + 5.3, above: false },
      { x: s.x, y: s.y - 5.4, above: true },
      { x: s.x, y: s.y + 7.0, above: false },
      { x: s.x, y: s.y - 7.1, above: true },
      { x: s.x - 2.6, y: s.y + 5.3, above: false },
      { x: s.x + 2.6, y: s.y + 5.3, above: false },
    ];
    const others = marks.filter((_, k) => k !== i);
    let best = tries[0];
    for (const t of tries) {
      const box = { x: t.x, y: t.y - 1.0, hw, hh };
      const hits = (o: { x: number; y: number; hw: number; hh: number }): boolean =>
        Math.abs(o.x - box.x) < o.hw + box.hw && Math.abs(o.y - box.y) < o.hh + box.hh;
      const clash = taken.some(hits) || others.some(hits);
      const room = poly.length >= 3 && inside(poly, { x: t.x, y: t.y }) && edgeDistance(poly, t) > 0.8;
      if (!clash && room) {
        best = t;
        break;
      }
      // Nothing perfect: keep the first that at least clears the other names.
      if (!clash && best === tries[0]) best = t;
    }
    taken.push({ x: best.x, y: best.y - 1.0, hw, hh });
    /**
     * The name and its town are warped *separately* — the town as part of the
     * site list, the name here — and the warp is two value-noise octaves that
     * do not agree about two points five units apart. On the old sheet the
     * disagreement topped out under a unit and nobody noticed; the wider sheet
     * reaches parts of the noise where it adds up, and a name drifted past
     * nine units from its own town — which on a small holding is the next
     * county over. So the warped name is kept on a leash to the warped town:
     * direction preserved, length clamped. 8.5 sits under the chart test's
     * nine-unit bound with the offset the tries table can produce.
     */
    const w = warp(best);
    const ws = warp(s);
    const dx = w.x - ws.x;
    const dy = w.y - ws.y;
    const d = Math.hypot(dx, dy);
    const MAX_DRIFT = 8.5;
    const k = d > MAX_DRIFT ? MAX_DRIFT / d : 1;
    out[i] = { x: ws.x + dx * k, y: ws.y + dy * k, above: best.above };
  }
  return out;
}

/** How far apart the hatching sits, per terrain. Mountains want air; marsh does not. */
const SPACING: Record<string, number> = {
  mountain: 5.4,
  hill: 4.8,
  forest: 3.8,
  marsh: 4.3,
  plain: 5.4,
  coast: 4.6,
  ruin: 5.0,
  // The continents' three: a jungle is the one ground denser than a wood; a
  // desert is mostly the absence of marks, which is what emptiness looks like
  // on a chart; ice sits as sparse as the plain so the white does the talking.
  jungle: 3.2,
  desert: 6.4,
  ice: 5.6,
};

/**
 * Jittered-grid scatter inside a cell. A jittered grid rather than a Poisson
 * disc because it is twenty lines instead of eighty and, at this density, the
 * eye cannot tell — what the eye *can* tell is a mark sitting half over a
 * border, or on the town, which is why everything is inset and cleared.
 *
 * The clearances are tried in three sizes and the *warped* distance from the
 * town is what decides, not the flat one. The warp is a smooth field but it is
 * not an isometry: it can pull a mark two units closer to the keep than it was
 * laid down, and on a board where the smallest holding is a dozen units across
 * that is the difference between hatching and a smudge over the walls.
 */
function scatter(poly: Pt[], site: Pt, seed: number): TerrainMark[] {
  const town = warp(site);
  const clear = (marks: TerrainMark[]): TerrainMark[] =>
    marks.filter((m) => Math.hypot(m.x - town.x, m.y - town.y) > 3.7);
  const tiers: Array<[number, number]> = [
    [MARK_INSET, TOWN_CLEARING],
    [MARK_INSET * 0.5, TOWN_CLEARING * 0.72],
    [MARK_INSET * 0.28, TOWN_CLEARING * 0.6],
  ];
  for (const [inset, clearing] of tiers) {
    const got = clear(scatterAt(poly, site, seed, inset, clearing));
    if (got.length > 0) return got.slice(0, 40);
  }
  /**
   * A cell too tight for any of that still gets its terrain drawn.
   *
   * A holding wedged between a deep bay and two neighbours can be a strip eight
   * units wide, and a strip with no hatching on it reads as a hole in the map
   * rather than as a small province. It is also where `openGround` looks for
   * somewhere to put a bandit camp, so an empty scatter is a marker with nowhere
   * to go. The outline pulled a third of the way toward the middle is always
   * inside the territory; the furthest such point from the town will do.
   */
  if (poly.length >= 3) {
    const mid = centroid(poly);
    let best: Pt | null = null;
    let bestD = 0;
    for (const v of poly) {
      const p = { x: v.x + (mid.x - v.x) * 0.34, y: v.y + (mid.y - v.y) * 0.34 };
      const w = warp(p);
      const d = Math.hypot(w.x - town.x, w.y - town.y);
      if (d > bestD) {
        bestD = d;
        best = w;
      }
    }
    if (best && bestD > 3.7) return [{ x: best.x, y: best.y, r: 1, v: 0 }];
  }
  return [];
}

function scatterAt(
  poly: Pt[],
  site: Pt,
  seed: number,
  inset: number,
  clearing: number,
): TerrainMark[] {
  if (poly.length < 3) return [];
  const terrain = PROVINCES[seed].terrain;
  const gap = SPACING[terrain] ?? 6;
  let x0 = Infinity;
  let x1 = -Infinity;
  let y0 = Infinity;
  let y1 = -Infinity;
  for (const p of poly) {
    x0 = Math.min(x0, p.x);
    x1 = Math.max(x1, p.x);
    y0 = Math.min(y0, p.y);
    y1 = Math.max(y1, p.y);
  }
  const out: TerrainMark[] = [];
  const cols = Math.ceil((x1 - x0) / gap);
  const rows = Math.ceil((y1 - y0) / gap);
  for (let r = 0; r < rows; r++) {
    for (let cx = 0; cx < cols; cx++) {
      const h = hash2(cx, r, seed * 131 + 7);
      const h2v = hash2(cx + 71, r + 31, seed * 131 + 7);
      const h3 = hash2(cx + 17, r + 53, seed * 131 + 7);
      // Row offset so the grid does not read as a grid.
      const p = {
        x: x0 + (cx + 0.5 + (r % 2) * 0.5) * gap + (h - 0.5) * gap * 0.66,
        y: y0 + (r + 0.5) * gap + (h2v - 0.5) * gap * 0.55,
      };
      if (!inside(poly, p)) continue;
      if (edgeDistance(poly, p) < inset) continue;
      // Leave the ground around the town bare: the name, the walls, the banner
      // and whatever the campaign layer wants to put there all live in it.
      if (dist(p, site) < clearing) continue;
      const w = warp(p);
      out.push({ x: w.x, y: w.y, r: 0.8 + h3 * 0.45, v: Math.floor(h3 * 4) % 4 });
    }
  }
  return out;
}


export function geometry(): Geometry {
  if (!cached) cached = build();
  return cached;
}

// ── What the map screen actually asks for ──────────────────────────────────

export function provinceSite(i: number): Pt {
  return geometry().sites[i] ?? { x: 0, y: 0 };
}

export function provinceCentre(i: number): Pt {
  return geometry().centres[i] ?? provinceSite(i);
}

export function provinceLabel(i: number): Pt & { above: boolean } {
  return geometry().labels[i] ?? { x: 0, y: 0, above: false };
}

export function provinceMarks(i: number): TerrainMark[] {
  return geometry().marks[i] ?? [];
}

/**
 * Does this holding stand on the water?
 *
 * Answered by `realm.ts` off the land mask, not by the drawing. There used to be
 * only one answer available and it was the drawing's — "the cell has an edge on
 * the coastline ring" — which was fine while nothing mechanical hung off it and
 * is not fine now that a port does. The realm and the chart must agree about
 * which provinces are on the sea, and the way to guarantee that is for one of
 * them to be the authority and the other to ask.
 */
export function isCoastal(i: number): boolean {
  return PROVINCES[i]?.coastal ?? false;
}

/** No road reaches it. There are two, and they are in the Sundered Sea. */
export function isIsland(i: number): boolean {
  return PROVINCES[i]?.island ?? false;
}

export function waveMarks(): Pt[] {
  return geometry().waves;
}

/**
 * How important a name is, 0 (a seat of a crown) to 3 (a village).
 *
 * A hundred and twenty-seven names cannot all be on the screen at once at the
 * zoom that shows the whole realm — they would be porridge, which is the exact
 * word for what the first attempt looked like. So the map screen shows names
 * down to a rank that depends on how far in it is zoomed, and this is the rank.
 * It is a property of the *place* rather than of the drawing, so it lives here
 * with the rest of the layout and not in the UI: what a chart puts in large type
 * is a cartographic decision.
 */
const LABEL_RANK: number[] = (() => {
  const capitals = new Set<string>(NATIONS.map((n) => n.capital));
  return PROVINCES.map((p) => {
    if (capitals.has(p.id)) return 0;
    if (p.fort >= 2 || p.wealth >= 15) return 1;
    if (p.fort >= 1 || p.wealth >= 12) return 2;
    return 3;
  });
})();

export function labelRank(i: number): number {
  return LABEL_RANK[i] ?? 3;
}

// ── Room left for the campaign layer ───────────────────────────────────────
//
// The map screen is going to grow markers it does not have yet: a warning that
// a province is about to be attacked, a hostile camp squatting on ground nobody
// holds, a host on the road between two holdings. Each of those needs somewhere
// to go that is not already occupied, and "somewhere" has to be decided once,
// here, rather than three times by three callers who cannot see each other.
//
//   · the flag flies to the upper *left* of the town mark
//   · the name sits below it, or above where the cell is short
//   · hosts present show as shields low left and low right
//   · the upper right is deliberately empty — that is `warningSlot`
//   · `openGround` is the emptiest spot in the territory, well clear of the
//     town, the border and the hatching: a camp belongs there
//   · a host mid-march belongs at `alongRoad(from, to, done)`

/** Upper right of the town mark. Reserved for an attack warning. */
export function warningSlot(i: number): Pt {
  const s = provinceSite(i);
  return { x: s.x + 4.3, y: s.y - 3.6 };
}

/**
 * The emptiest ground in a province, for anything that is not the town.
 *
 * The hatching marks are the first place to look, because they are by
 * construction inside the cell, clear of the borders and clear of the keep. But
 * a cramped cell may have only a handful of them and all of them close in, so
 * the cell's own outline is offered as well: each vertex drawn a quarter of the
 * way back toward the middle is a point that is inside the territory and as far
 * from the town as the territory goes.
 */
const openGroundCache = new Map<number, Pt>();

export function openGround(i: number): Pt {
  const hit = openGroundCache.get(i);
  if (hit) return hit;
  const g = geometry();
  const site = provinceSite(i);
  const ring = g.cells[i] ?? [];
  const centre = g.centres[i] ?? site;

  let best = centre;
  let score = dist(centre, site);
  const offer = (p: Pt, mustBeInside: boolean): void => {
    const s = dist(p, site);
    if (s <= score) return;
    if (mustBeInside && ring.length > 2 && !inside(ring, p)) return;
    score = s;
    best = { x: p.x, y: p.y };
  };
  for (const m of g.marks[i] ?? []) offer(m, false);
  if (score < 4.2) {
    for (const v of ring) {
      offer({ x: v.x + (centre.x - v.x) * 0.28, y: v.y + (centre.y - v.y) * 0.28 }, true);
    }
  }
  openGroundCache.set(i, best);
  return best;
}

/** An SVG `d` for a polyline, closed or not, rounded to keep the markup small. */
export function toPath(points: Pt[], close = false): string {
  if (points.length === 0) return '';
  let d = `M${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
  for (let i = 1; i < points.length; i++) d += `L${points[i].x.toFixed(2)} ${points[i].y.toFixed(2)}`;
  return close ? `${d}Z` : d;
}

const cellPaths = new Map<number, string>();
export function cellPath(i: number): string {
  const hit = cellPaths.get(i);
  if (hit !== undefined) return hit;
  const d = toPath(geometry().cells[i] ?? [], true);
  cellPaths.set(i, d);
  return d;
}

/**
 * The land, as one fillable path: every landmass, plus every piece of water any
 * of them encloses, wound the other way.
 *
 * The rings come straight off the traced boundary between land and water, so a
 * fill of this path is *exactly* the dry ground — the inland seas and the great
 * middle sea are holes in it under SVG's default non-zero rule, with no polygon
 * union anywhere and nothing for a caller to do differently. Stroking it draws
 * the coastline of the world, and it is the same polyline the province standing
 * on that shore stops at.
 */
let coastPathCache = '';
export function coastPath(): string {
  if (!coastPathCache) {
    coastPathCache = geometry()
      .coast.filter((r) => r.length >= 3)
      .map((r) => toPath(r, true))
      .join('');
  }
  return coastPathCache;
}

/** The same, for a drop shadow. The holes belong in the shadow too. */
export function coastOutlinePath(): string {
  return coastPath();
}

/**
 * There are no water cells any more.
 *
 * The chart used to give every cell of interior sea a Voronoi site of its own so
 * that a partition of the province sites would not sprawl across it. The
 * partition is now traced off `realm.ts`'s raster, which knows perfectly well
 * where the water is, so the water needs no cells and gets none. Kept as an
 * empty list because it is a published shape and a caller asking for it should
 * get "nothing to paint" rather than a crash. Paint the sea by filling `VIEW`
 * and drawing `coastPath()` over it.
 */
export function seaCellPaths(): string[] {
  return [];
}

/**
 * Every stretch where a province meets water, from the land side.
 *
 * The coastline of the interior seas, in other words, plus the open-ocean edge
 * of the outermost cells. Draw it heavier than a shire border and lighter than a
 * national frontier; `a` is the province and `b` is always -1.
 */
let shorePathCache: Array<{ a: number; d: string }> | null = null;
export function shorePaths(): Array<{ a: number; d: string }> {
  if (!shorePathCache) {
    shorePathCache = geometry().shores.map((s) => ({ a: s.a, d: toPath(s.points) }));
  }
  return shorePathCache;
}

/**
 * The engraved sea lines, one path per depth — each path holding every contour
 * at that depth, so a caller strokes or fills four paths rather than four
 * hundred. Filling one paints a band of shallows round every landmass at once.
 */
let echoPathCache: string[] | null = null;
export function echoPaths(): string[] {
  if (!echoPathCache) {
    echoPathCache = geometry().echoes.map((level) =>
      level
        .filter((r) => r.length >= 3)
        .map((r) => toPath(r, true))
        .join(''),
    );
  }
  return echoPathCache;
}

let borderPathCache: Array<{ a: number; b: number; d: string }> | null = null;
export function borderPaths(): Array<{ a: number; b: number; d: string }> {
  if (!borderPathCache) {
    borderPathCache = geometry().borders.map((s) => ({ a: s.a, b: s.b, d: toPath(s.points) }));
  }
  return borderPathCache;
}

// ── Roads ──────────────────────────────────────────────────────────────────

/**
 * A road between two holdings: a bent, warped polyline that stops short of both
 * towns so the town mark is never drawn over.
 *
 * The bend comes out of a hash of the pair, not out of the pair's geometry, so
 * two roads leaving the same town do not bend the same way. The `campaign`
 * layer can put a marching host anywhere along one of these by asking for the
 * points and walking them — which is why this returns the polyline and a `d`
 * rather than only the string.
 */
const roadCache = new Map<string, Pt[]>();

export function roadPoints(a: number, b: number): Pt[] {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  const key = `${lo}-${hi}`;
  const hit = roadCache.get(key);
  if (hit) return hit;

  const p = SITES[lo];
  const q = SITES[hi];
  const len = dist(p, q);
  const ux = (q.x - p.x) / len;
  const uy = (q.y - p.y) / len;
  // Stop clear of the town marks at both ends.
  const trim = 3.2;
  const from = { x: p.x + ux * trim, y: p.y + uy * trim };
  const to = { x: q.x - ux * trim, y: q.y - uy * trim };
  // A road bends; it does not writhe. The first draw used three times this and
  // every route came out an S that doubled back on itself.
  const bend = (hash2(lo, hi, 991) - 0.5) * 2 * 0.085 * len;
  const bow = (hash2(hi, lo, 55) - 0.5) * 2 * 0.03 * len;

  const out: Pt[] = [];
  const steps = Math.max(6, Math.round(len / 2.4));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    // A quadratic arch plus a gentler S, both perpendicular to the road.
    const off = bend * 4 * t * (1 - t) + bow * Math.sin(t * Math.PI * 2);
    out.push(
      warp({
        x: from.x + (to.x - from.x) * t - uy * off,
        y: from.y + (to.y - from.y) * t + ux * off,
      }),
    );
  }
  roadCache.set(key, out);
  return out;
}

export function roadPath(a: number, b: number): string {
  return toPath(roadPoints(a, b));
}

// ── Sea lanes ──────────────────────────────────────────────────────────────
//
// A lane is not a straight line between two harbours, and it must not be drawn
// as one: half of them would cross a headland and the chart would be telling the
// player something the simulation does not believe. `realm.ts` routes every lane
// through the water cells it actually passes through — that is where the shape
// comes from — and this turns that list of cells into something drawable:
// smoothed, trimmed clear of both town marks, and warped like everything else so
// it sits on the same distorted plane as the coast it follows.

const laneCache = new Map<string, Pt[]>();
const LANE_BY_PAIR = new Map<string, { months: number; route: Pt[] }>();
for (const l of SEA_LANES) {
  LANE_BY_PAIR.set(`${l.a}-${l.b}`, { months: l.months, route: l.route.map((p) => ({ ...p })) });
}

/** Chaikin, twice: a chain of lattice cells becomes a course a ship could hold. */
function smooth(points: Pt[]): Pt[] {
  let out = points;
  for (let pass = 0; pass < 2 && out.length > 2; pass++) {
    const next: Pt[] = [out[0]];
    for (let i = 0; i < out.length - 1; i++) {
      const a = out[i];
      const b = out[i + 1];
      next.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
      next.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
    }
    next.push(out[out.length - 1]);
    out = next;
  }
  return out;
}

/**
 * The course of a lane, as a warped polyline running from `min(a,b)` to
 * `max(a,b)`. Empty when the two are not joined by sea.
 */
export function seaLanePoints(a: number, b: number): Pt[] {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  const key = `${lo}-${hi}`;
  const hit = laneCache.get(key);
  if (hit) return hit;

  const lane = LANE_BY_PAIR.get(key);
  const from = SITES[lo];
  const to = SITES[hi];
  if (!lane || !from || !to) {
    laneCache.set(key, []);
    return [];
  }

  // The harbours themselves are the ends; the water cells are the middle. Both
  // ends are pulled a little out to sea so the line leaves the town rather than
  // starting on top of it.
  const chain: Pt[] = [from, ...lane.route, to];
  const trimmed = smooth(chain).map(warp);
  const out = trimEnds(trimmed, 3.4);
  laneCache.set(key, out);
  return out;
}

/** Drop the first and last few units of a polyline, so it clears the town marks. */
function trimEnds(points: Pt[], by: number): Pt[] {
  if (points.length < 3) return points;
  const drop = (pts: Pt[]): Pt[] => {
    let spent = 0;
    let i = 0;
    while (i < pts.length - 2 && spent < by) {
      spent += dist(pts[i], pts[i + 1]);
      i++;
    }
    return pts.slice(i);
  };
  return drop(drop(points).reverse()).reverse();
}

export function seaLanePath(a: number, b: number): string {
  return toPath(seaLanePoints(a, b));
}

/** Where a fleet a fraction of the way along a lane belongs. */
export function alongSeaLane(a: number, b: number, t: number): Pt {
  const pts = seaLanePoints(a, b);
  if (pts.length < 2) return pts[0] ?? { x: 0, y: 0 };
  // The lane is stored low-index first; a voyage may be running the other way.
  const f = a <= b ? t : 1 - t;
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += dist(pts[i - 1], pts[i]);
  let want = Math.max(0, Math.min(1, f)) * total;
  for (let i = 1; i < pts.length; i++) {
    const seg = dist(pts[i - 1], pts[i]);
    if (want <= seg || i === pts.length - 1) {
      const k = seg > 0 ? want / seg : 0;
      return {
        x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * k,
        y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * k,
      };
    }
    want -= seg;
  }
  return pts[pts.length - 1];
}

/** The middle of a lane, for a label or a blockade mark. */
export function seaLaneMid(a: number, b: number): Pt {
  return alongSeaLane(Math.min(a, b), Math.max(a, b), 0.5);
}

/**
 * A harbour mark, on the seaward side of the town.
 *
 * The four slots round a town mark were spoken for before the sea existed —
 * banner upper left, name below, host shields low left and low right, warning
 * upper right. A port has nowhere to go among them, so it does not take a slot:
 * it is drawn *off* the town, a short way toward the nearest water, which is
 * also the only place a harbour has ever been built.
 */
const portSlotCache = new Map<number, Pt>();

export function portSlot(i: number): Pt {
  const hit = portSlotCache.get(i);
  if (hit) return hit;
  const s = provinceSite(i);
  const g = geometry();
  let best: Pt | null = null;
  let bestD = Infinity;
  for (const seg of g.shores) {
    if (seg.a !== i) continue;
    for (const p of seg.points) {
      const d = dist(p, s);
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
  }
  // Two thirds of the way to the water, and never further than a town's width.
  const reach = Math.min(6.4, Math.max(3.6, bestD * 0.66));
  const out = best
    ? { x: s.x + ((best.x - s.x) / (bestD || 1)) * reach, y: s.y + ((best.y - s.y) / (bestD || 1)) * reach }
    : { x: s.x - 4.3, y: s.y - 3.6 };
  portSlotCache.set(i, out);
  return out;
}

/** The midpoint of a road, where a crossing mark or a marching host belongs. */
export function roadMid(a: number, b: number): Pt {
  const pts = roadPoints(a, b);
  return pts[Math.floor(pts.length / 2)] ?? { x: 0, y: 0 };
}

/**
 * A point a fraction of the way along a road, by arc length. Left here for the
 * campaign layer: an army that is between two provinces is drawn at `along`
 * equal to how much of its march it has done.
 */
export function alongRoad(a: number, b: number, t: number): Pt {
  const pts = roadPoints(a, b);
  if (pts.length < 2) return pts[0] ?? { x: 0, y: 0 };
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += dist(pts[i - 1], pts[i]);
  let want = Math.max(0, Math.min(1, t)) * total;
  for (let i = 1; i < pts.length; i++) {
    const seg = dist(pts[i - 1], pts[i]);
    if (want <= seg || i === pts.length - 1) {
      const f = seg > 0 ? want / seg : 0;
      return { x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * f, y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * f };
    }
    want -= seg;
  }
  return pts[pts.length - 1];
}
